import React, { useRef, useEffect } from 'react';
import profilePhoto from '../images/Dmitriy_Profile.jpg';
import './Overview.css';

export default function Overview({ isOpen, onClose }) {
  const panelRef = useRef(null);

  // Focus the panel when it opens so Escape key works
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);
  return (
    <>
      {/* Backdrop */}
      <div
        className={`ov-backdrop ${isOpen ? 'ov-backdrop--visible' : ''}`}
        onClick={onClose}
      />

      {/* Panel */}
      <aside
        ref={panelRef}
        className={`ov-panel ${isOpen ? 'ov-panel--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Quick Overview"
        tabIndex={-1}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      >
        <div className="ov-paper">

          {/* Close button */}
          <button className="ov-close" onClick={onClose} title="Close overview">✕</button>

          {/* Newspaper masthead */}
          <div className="ov-masthead">
            <div className="ov-masthead__rule" />
            <p className="ov-masthead__label">Quick Overview</p>
            <h1 className="ov-masthead__name">Dmitriy Filimonov</h1>
            <p className="ov-masthead__title">
              Solutions Engineer &nbsp;·&nbsp; Full-Stack Engineering &nbsp;·&nbsp; Client-Facing Delivery
            </p>
            <div className="ov-masthead__rule" />
            {/* Author photo */}
            <div className="ov-photo-frame">
              <div className="ov-photo-corner ov-photo-corner--tl" />
              <div className="ov-photo-corner ov-photo-corner--tr" />
              <div className="ov-photo-corner ov-photo-corner--bl" />
              <div className="ov-photo-corner ov-photo-corner--br" />
              <img src={profilePhoto} alt="Dmitriy Filimonov" className="ov-photo" />
            </div>

            <div className="ov-masthead__subline">
              <span>UC Merced · B.S. CSE · 2026</span>
              <span className="ov-masthead__dot">◆</span>
              <span>UC Berkeley · CS161 · Summer 2026</span>
              <span className="ov-masthead__dot">◆</span>
              <span>Santa Clara, CA</span>
            </div>
            <div className="ov-masthead__rule" />
          </div>

          {/* Two-column newspaper layout */}
          <div className="ov-columns">

            {/* ── LEFT COLUMN ── */}
            <div className="ov-col">

              {/* Tech stack -- ticker style */}
              <div className="ov-section">
                <h2 className="ov-section__head">Stack</h2>
                <div className="ov-tags">
                  {['Python','FastAPI','Flask','PostgreSQL','React','React Native',
                    'JavaScript','Node.js','Docker','Git','SQLAlchemy','Canvas API','JUnit','C/C++','Go'].map(t => (
                    <span key={t} className="ov-tag">{t}</span>
                  ))}
                </div>
              </div>

              <div className="ov-col-rule" />

              {/* Featured project 1 */}
              <div className="ov-section">
                <h2 className="ov-section__head">Client Work</h2>
                <h3 className="ov-project__title">HarvestLink: Farm Traceability System</h3>
                <p className="ov-project__client">Langer Farms · Internship · Jan–May 2026 · Private repository — available on request</p>
                <ul className="ov-bullets">
                  <li>Led 4-person team delivering a "Seed to Harvest" traceability platform replacing manual farm records</li>
                  <li>Designed PostgreSQL schema tracking Block/Line → Bin → Truck → Packing House lineage with immutable integrity</li>
                  <li>Built offline-capable React Native mobile app with QR scanning for field workers</li>
                  <li>FastAPI + SQLAlchemy REST backend with 30-test suite; containerized with Docker for client handoff</li>
                </ul>
                <p className="ov-project__stack">FastAPI · PostgreSQL · React Native · Docker</p>
              </div>

              <div className="ov-col-rule" />

              {/* Contact links */}
              <div className="ov-section">
                <h2 className="ov-section__head">Contact</h2>
                <div className="ov-links">
                  <a href="mailto:dmitriyfilimonov33@gmail.com" className="ov-link">
                    <span className="ov-link__label">Email</span>
                    <span className="ov-link__value">dmitriyfilimonov33@gmail.com</span>
                  </a>
                  <a href="https://linkedin.com/in/dmitriy-filimonov/" target="_blank" rel="noopener noreferrer" className="ov-link">
                    <span className="ov-link__label">LinkedIn</span>
                    <span className="ov-link__value">linkedin.com/in/dmitriy-filimonov</span>
                  </a>
                  <a href="https://github.com/dtech982" target="_blank" rel="noopener noreferrer" className="ov-link">
                    <span className="ov-link__label">GitHub</span>
                    <span className="ov-link__value">github.com/dtech982</span>
                  </a>
                  <a href="tel:+14083728844" className="ov-link">
                    <span className="ov-link__label">Phone</span>
                    <span className="ov-link__value">408-372-8844</span>
                  </a>
                </div>
              </div>

            </div>

            {/* Column divider */}
            <div className="ov-divider" />

            {/* ── RIGHT COLUMN ── */}
            <div className="ov-col">

              {/* About -- brief */}
              <div className="ov-section">
                <h2 className="ov-section__head">About</h2>
                <p className="ov-body">
                  Full-stack engineer and CS&amp;E graduate of UC Merced, 2026.
                  I build full-stack platforms and autonomous systems with a focus on
                  architectural integrity and production-grade reliability. Diagnostic by
                  nature -- I identify root structure before writing a line of code.
                  Security runs through my work, from a cryptographic file-sharing system
                  built in Go at UC Berkeley to vulnerability remediation and
                  Content-Security-Policy hardening in production projects.
                </p>
              </div>

              <div className="ov-col-rule" />

              {/* Project 2 */}
              <div className="ov-section">
                <h2 className="ov-section__head">Projects</h2>
                <h3 className="ov-project__title">CS161: Secure File-Sharing System</h3>
                <p className="ov-project__client">UC Berkeley · Summer 2026 · Private repository — available on request</p>
                <ul className="ov-bullets">
                  <li>Five-phase secure file-sharing system in Go: Argon2 authentication, encrypted storage and append, public-key sharing, and access revocation</li>
                  <li>Constant-size metadata so appends cost O(append size) instead of re-encrypting the whole file</li>
                  <li>30/30 local tests, 24/24 autograder tests, and a 25-vector security audit with zero vulnerabilities found</li>
                </ul>
                <p className="ov-project__stack">Go · Cryptography · Testing · Security Audit</p>
              </div>

              <div className="ov-col-rule" />

              {/* Project 3 */}
              <div className="ov-section">
                <h3 className="ov-project__title">ARTyping Template Generator</h3>
                <p className="ov-project__client">Web Tool · 2025 – Present</p>
                <ul className="ov-bullets">
                  <li>Real-time Canvas API image-to-ASCII engine with HiDPI rendering (2–3x pixel ratio) and DPI-accurate paper modeling</li>
                  <li>Run-length encoding and space counting guide system with colored overlays and full-width margin-to-margin row encoding</li>
                  <li>Input validation suite backed by 93 automated Jest tests</li>
                </ul>
                <p className="ov-project__stack">React · Canvas API · JavaScript · Jest</p>
              </div>

              <div className="ov-col-rule" />

              {/* Project 4 */}
              <div className="ov-section">
                <h3 className="ov-project__title">Personal Portfolio</h3>
                <p className="ov-project__client">dmitriyfilimonov.com · 2025 – Present</p>
                <ul className="ov-bullets">
                  <li>Custom React routing via URL query params — no router library required</li>
                  <li>Interactive business card, QR generator, and ARTyping tool as standalone pages</li>
                  <li>CSP headers, file upload validation, canvas size guards, and session sanitization</li>
                </ul>
                <p className="ov-project__stack">React · JavaScript · Vercel</p>
              </div>

              <div className="ov-col-rule" />

              {/* Skills summary */}
              <div className="ov-section">
                <h2 className="ov-section__head">Skills</h2>
                <div className="ov-skill-row">
                  <span className="ov-skill-cat">Languages</span>
                  <span className="ov-skill-list">Python · Java · C/C++ · Go · JavaScript · SQL · R</span>
                </div>
                <div className="ov-skill-row">
                  <span className="ov-skill-cat">Frameworks</span>
                  <span className="ov-skill-list">React · FastAPI · Flask · Node.js · SQLAlchemy · Canvas API · Docker · JUnit</span>
                </div>
                <div className="ov-skill-row">
                  <span className="ov-skill-cat">Security</span>
                  <span className="ov-skill-list">Cryptography · Authentication · Access Control · Security Auditing · CSP</span>
                </div>
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="ov-footer">
            <div className="ov-masthead__rule" />
            <p className="ov-footer__text">
              <a href="#contact" onClick={onClose}>Send a message ↓</a>
              &nbsp;·&nbsp;
              <a href="https://github.com/dtech982" target="_blank" rel="noopener noreferrer">GitHub</a>
              &nbsp;·&nbsp;
              <a href="https://linkedin.com/in/dmitriy-filimonov/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            </p>
          </div>

        </div>
      </aside>
    </>
  );
}